/**
 * Centralised tracking config — the single source of truth for every tracking
 * ID, per-exam mapping, and click-identifier definition. Adding an exam or a
 * platform should be one edit here: the browser tag (GoogleTags), the server
 * Measurement Protocol (ga4-server), the checkout metadata writer
 * (create-checkout), and the Google Ads Data Manager upload (google-ads-server)
 * all read from this module so they can never drift apart.
 *
 * Safe to import from both client and server: it only reads `NEXT_PUBLIC_*`
 * (inlined at build) and exposes pure constants/functions. `getConversionActionId`
 * reads server-only env, but is only ever called server-side (the webhook).
 */

import type { ExamSlug, PlanKey } from '@/config/pricing'
import type { Industry } from '@/types/exam'

// ── Google tag IDs (browser) ──
// NEXT_PUBLIC_* is inlined at build. The server reads these SAME constants — for
// the `_ga_<id>` session-cookie name and the Measurement Protocol measurement_id
// — so the client tag and every server reader resolve to one GA4 property.
export const GA4_MEASUREMENT_ID =
  process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID ?? 'G-079FLB2BXM'

export const GOOGLE_ADS_ID =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? 'AW-18087942283'

// ── Meta (Facebook/Instagram) dataset ──
// ONE dataset for the whole domain (META_TRACKING_PLAN.md §1) — the dataset ID
// and the legacy "pixel id" are the same value. No hardcoded default: the
// dataset doesn't exist until the account runbook (§2) completes, and every
// tag/sender must silently skip while these are unset.
export const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID ?? ''

/**
 * Server-side dataset id for CAPI calls. Prefers META_DATASET_ID, falls back
 * to the public pixel id (identical value by definition). Returns undefined
 * when unset so callers warn-and-skip, mirroring getConversionActionId.
 */
export function getMetaDatasetId(): string | undefined {
  return process.env.META_DATASET_ID || META_PIXEL_ID || undefined
}

/**
 * Graph/Marketing API version for CAPI. Verified live 2026-08-21 against the
 * changelog (v26.0 released 2026-07-29). Marketing API versions expire in
 * ~8–12 months — re-verify and bump annually (META_TRACKING_PLAN.md §15 flag 1).
 */
export const META_API_VERSION = 'v26.0'

// Meta's first-party cookies (set by fbevents.js on our domain) plus our
// durable fbclid cookie. _fbc exists only if the visitor landed with an fbclid
// while the pixel was loaded, so _ps_fbclid (server-set per C-C2 — Safari ITP
// caps JS-set cookies at 7 days) is the durable fallback. It stores the
// capture timestamp too: a server-built fbc needs the FIRST-observed time
// (fb.1.{captureMs}.{fbclid}), never send time.
export const META_COOKIES = {
  fbp: '_fbp',
  fbc: '_fbc',
} as const

export const FBCLID_COOKIE = '_ps_fbclid'

// ── Product ID registry ──
// `{examSlug}-{planKey}` is THE product id everywhere: Meta content_ids /
// contents[].id, GA4 items[].item_id, remarketing ids, and any future Meta
// catalog / Merchant Center feed. Never Stripe price IDs — they rotate on
// every price experiment and would silently orphan custom-conversion rules,
// product audiences, and feeds (master plan §2.1).
export function productId(examSlug: ExamSlug, planKey?: PlanKey): string {
  return planKey ? `${examSlug}-${planKey}` : examSlug
}

// Meta content_category values — industry-level ON PURPOSE: per-exam identity
// lives in content_ids + URL rules, and industry slugs survive new exams in a
// vertical without taxonomy churn (master plan §2.2). lowercase/underscores
// only — these feed custom-conversion rules and audience refinement, which
// reject spaces. Keep values certification-neutral (health-classifier defense,
// META_TRACKING_PLAN.md §8).
export const CONTENT_CATEGORY_BY_INDUSTRY: Record<Industry, string> = {
  'Human Resources': 'human_resources',
  Nursing: 'nursing',
  'Project Management': 'project_management',
  Medical: 'medical',
  'Speech-Language Pathology': 'speech_language_pathology',
  Counseling: 'counseling',
  'College Admissions': 'college_admissions',
}

// ── Microsoft Clarity (browser) ──
// Session replay + heatmaps. The project id is public — it ships in the client
// tag — so a hardcoded default is safe; NEXT_PUBLIC_CLARITY_PROJECT_ID lets a
// preview/staging env point at a different Clarity project (or blank it out).
export const CLARITY_PROJECT_ID =
  process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID ?? 'xjesuwvzal'

// ── FirstPromoter (affiliate) ──
// Company id (cid) for the fpr.js referral tracking script. Hardcoded default like
// the Clarity/GA ids above — it's a PUBLIC id that ships in client JS, so a default
// is safe and means the tag works on any deploy with no env step. Override with
// NEXT_PUBLIC_FIRSTPROMOTER_CID (e.g. set it blank on staging/preview to keep test
// traffic out of FirstPromoter).
export const FIRSTPROMOTER_CID =
  process.env.NEXT_PUBLIC_FIRSTPROMOTER_CID ?? 'ypifnd8k'

// fpr.js drops this cookie carrying the referral tracking id (tid). create-checkout
// reads it server-side and passes it to Stripe as client_reference_id; the
// FirstPromoter Stripe app matches that back to the referring promoter. Verify the
// exact name in FirstPromoter → Setup if attribution ever misses (their docs also
// reference `_fprom_ref` alongside it).
export const FIRSTPROMOTER_TID_COOKIE = '_fprom_tid'

// ── Per-exam tracking ──
// Exams that have their own Google Ads conversion action so Smart Bidding can
// optimise each independently. `shrm-book` is a separate product, intentionally
// outside the ad-conversion scope. Order matches GOOGLE_ADS_SETUP.md.
export const TRACKED_EXAMS = [
  'shrm-cp',
  'shrm-scp',
  'phr',
  'sphr',
  'aphr',
  'ccrn',
  'pmp',
] as const

export type TrackedExam = (typeof TRACKED_EXAMS)[number]

export function isTrackedExam(exam: string): exam is TrackedExam {
  return (TRACKED_EXAMS as readonly string[]).includes(exam)
}

/**
 * Env var name holding an exam's Google Ads conversion action id. The slug is
 * upper-cased with non-alphanumerics turned to underscores
 * (`shrm-cp` → `GOOGLE_ADS_CONVERSION_ACTION_ID_SHRM_CP`).
 */
export function conversionActionEnvKey(exam: string): string {
  const slug = exam.toUpperCase().replace(/[^A-Z0-9]+/g, '_')
  return `GOOGLE_ADS_CONVERSION_ACTION_ID_${slug}`
}

/**
 * The per-exam Google Ads conversion action id, resolved from env. Returns
 * undefined when that exam's action isn't configured yet (the upload then skips
 * that exam rather than firing into the wrong action). Server-side only.
 */
export function getConversionActionId(exam: string): string | undefined {
  return process.env[conversionActionEnvKey(exam)] || undefined
}

// ── First/last-touch cookies ──
// JSON attribution snapshots written by useAttributionCapture (read/write lives
// in utils/attribution.ts). The client posts their parsed contents in the
// create-checkout body; the server falls back to reading these same cookies off
// the request when the body carries none, so a present cookie can never yield a
// session with zero ft_/lt_ metadata.
export const FIRST_TOUCH_COOKIE = '_ps_first_touch'
export const LAST_TOUCH_COOKIE = '_ps_last_touch'

// ── Click identifiers ──
// Google click ids that match an offline conversion back to the ad click. We
// persist whichever the URL carries into a durable first-party cookie so it
// survives later non-ad return visits, then forward it to Stripe metadata and
// finally into the Data Manager upload's `adIdentifiers`.
//   - gclid:  standard web ad click
//   - gbraid: iOS web-to-app / app campaigns (privacy-safe, replaces gclid)
//   - wbraid: iOS web campaigns under ATT (privacy-safe, replaces gclid)
// An ad click carries exactly one of these; the upload prefers gclid, then
// gbraid, then wbraid.
export const CLICK_ID_TTL_DAYS = 90

export const CLICK_ID_COOKIES = {
  gclid: '_ps_gclid',
  gbraid: '_ps_gbraid',
  wbraid: '_ps_wbraid',
} as const

export type ClickIdKind = keyof typeof CLICK_ID_COOKIES

export const CLICK_ID_KINDS = Object.keys(CLICK_ID_COOKIES) as ClickIdKind[]
